
import { useState } from "react";
import { NotificationFormData, AIAnalysisResult, NotificationProductData } from "@/types/notifications";

export const useNotificationForm = (userId: string | undefined, userName: string | undefined) => {
  // Initialize form data with default values
  const [notificationForm, setNotificationForm] = useState<NotificationFormData>({
    type: "", // Changed from "order" to empty string
    voNumber: "",
    exactOrderNumber: "",
    message: "",
    priority: "normaal",
    userId: userId || "",
    userName: userName || "",
    status: "", // Kept as empty string
    recipients: {
      internal: {
        group1: false,
        group2: false
      },
      external: [] as string[]
    },
    products: [] as NotificationProductData[]
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle both string and boolean values
  const handleCheckboxChange = (name: string, value: string | boolean) => {
    if (name === 'priority') {
      // If value is boolean, convert it to appropriate string
      const priorityValue = typeof value === 'boolean'
        ? (value ? 'hoog' : 'normaal')
        : value;
      
      setNotificationForm(prev => ({
        ...prev,
        priority: priorityValue
      }));
    } else {
      setNotificationForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleInternalCheckboxChange = (group: 'group1' | 'group2') => {
    setNotificationForm(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        internal: {
          ...prev.recipients.internal,
          [group]: !prev.recipients.internal[group]
        }
      }
    }));
  };

  const toggleExternalRecipient = (recipient: string) => {
    setNotificationForm(prev => {
      const isSelected = prev.recipients.external.includes(recipient);
      return {
        ...prev,
        recipients: {
          ...prev.recipients,
          external: isSelected
            ? prev.recipients.external.filter(r => r !== recipient)
            : [...prev.recipients.external, recipient]
        }
      };
    });
  };

  const handleProductsChange = (products: NotificationProductData[]) => {
    setNotificationForm(prev => ({
      ...prev,
      products
    }));
  };

  const handleAIAnalysisComplete = (data: AIAnalysisResult) => {
    setNotificationForm(prev => ({
      ...prev,
      message: data.formattedText || prev.message,
      voNumber: data.voNumber || prev.voNumber
    }));
  };

  return {
    notificationForm,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleProductsChange,
    handleAIAnalysisComplete
  };
};
